import { Slide } from "@jelly/lesson";
import config from "appConfig";

const principle: Slide = {
  title: "Definition and characterizing methods",
  layout: {
    type: "customCase010102",
    options: {
      views: [
        {
          description: "A <b>multi-port</b> is a system with several inputs (ports).",
          images: [
            `${config.ASSETS_URL}/01/case-01/slide02-view01-image01.svg`,
            `${config.ASSETS_URL}/01/case-01/slide02-view01-image02.svg`,
            `${config.ASSETS_URL}/01/case-01/slide02-view01-image03.svg`,
          ],
        },
        {
          list: [
            "Multi-port characterization allows linking together the physical quantities at the different inputs.",
            "A multi-port can be characterized by a matrix (impedance matrix, admittance matrix, scattering matrix, etc.).",
            "Characterizing a multi-port consists in determining this matrix",
          ],
          image: `${config.ASSETS_URL}/01/case-01/slide02-view02-image.svg`,
          description: `4-port with
          <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <msub>
              <mi>p</mi>
              <mi>i</mi>
            </msub>
         </math>
         pressureand volume velocity
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <msub>
              <mi>U</mi>
              <mi>i</mi>
            </msub>
         </math>
         at the inputs for
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
           <mi>i</mi>
           <mo>∈</mo>
           <mo>{</mo>
           <mn>1</mn>
           <mo>,</mo>
           <mn>2</mn>
           <mo>,</mo>
           <mn>3</mn>
           <mo>,</mo>
           <mn>4</mn>
           <mo>}</mo>
         </math>.`,
        },
        {
          image: `${config.ASSETS_URL}/01/case-01/slide02-view03-image.svg`,
          list: [
            "The matrix of a N-port has N² terms (unknowns) but the matrix relation has only N equations.",
            "To obtain N equations, it is therefore necessary to measure the pressures and volume velocities in different situations (states).",
            "At least N different and independent situations are needed.",
            "To measure the state of the system at a port an “impedance head” is used which measures the couple (p, U). ",
            "Or else some (at least 2) impedances assumed to be known can be put on some inputs.",
            "Some sources are also needed which can be included in the impedance heads.",
          ],
        },
        {
          image: `${config.ASSETS_URL}/01/case-01/slide02-view04-image.svg`,
          list: [
            "In that case it can be considered that there is one input and one output (i.e., one of the two inputs is considered as an output)",
            "It is characterized by a 2x2 matrix:",
          ],
          imageEquations: [
            `${config.ASSETS_URL}/01/case-01/slide02-view04-equation01.svg`,
            `${config.ASSETS_URL}/01/case-01/slide02-view04-equation02.svg`,
            `${config.ASSETS_URL}/01/case-01/slide02-view04-equation03.svg`,
            `${config.ASSETS_URL}/01/case-01/slide02-view04-equation04.svg`,
          ],
        },
        {
          list: [
            "The sought matrix has 4 terms (unknowns).",
            "To obtain 4 equations, it is necessary to measure the pressures and flow rates in 2 different and independent situations:",
            "In this case, 2 impedance heads and 2 measurements in independent situations are required (e.g., symmetrical and antisymmetrical excitations).",
          ],
          imageEquations: [
            `${config.ASSETS_URL}/01/case-01/slide02-view05-equation01.svg`,
            `${config.ASSETS_URL}/01/case-01/slide02-view05-equation02.svg`,
          ],
          images: [
            `${config.ASSETS_URL}/01/case-01/slide02-view05-image01.svg`,
            `${config.ASSETS_URL}/01/case-01/slide02-view05-image02.svg`,
          ],
        },
        {
          image: `${config.ASSETS_URL}/01/case-01/slide02-view06-image.svg`,
          list: [
            "Using a single impedance head is also possible but leads to one equation per measurement.",
            "To obtain 4 equations in this case, it is necessary to perform 4 measurements with either:",
          ],
          subList: [
            "4 known impedances",
            "2 known impedances and the impedance head with the source on each side of the 2-port",
          ],
        },
        {
          list: [
            "If the two-port is reciprocal and moreover symmetrical, only two terms suffice to characterize it, since the following is true",
            "In that case, the transfer matrix takes the form",
            `where
            <span style="font-size: 16px">
              <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                <msub>
                  <mi>Z</mi>
                  <mrow>
                    <mi>e</mi>
                    <mi>q</mi>
                  </mrow>
                </msub>
              </math>
            </span>
            and
            <span style="font-size: 16px">
              <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                <msub>
                  <mi>k</mi>
                  <mrow>
                    <mi>e</mi>
                    <mi>q</mi>
                  </mrow>
                </msub>
              </math>
            </span>
            are the equivalent characteristic impedance and wave number, respectively, and
            <span style="font-size: 16px">
              <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                <mi>L</mi>
              </math>
            </span>
            is an arbitrary length.`,
          ],
          subList: [
            `For the scattering matrix we have
            <span style="font-size: 16px">
              <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                <msup>
                  <mi>R</mi>
                  <mo scriptlevel=0>+</mo>
                </msup>
                <mo>=</mo>
                <msup>
                  <mi>R</mi>
                  <mo scriptlevel=0>-</mo>
                </msup>
                <mo>=</mo>
                <mi>R</mi>
              </math>
            </span>
            and
            <span style="font-size: 16px">
              <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                <msup>
                  <mi>T</mi>
                  <mo scriptlevel=0>+</mo>
                </msup>
                <mo>=</mo>
                <msup>
                  <mi>T</mi>
                  <mo scriptlevel=0>-</mo>
                </msup>
                <mo>=</mo>
                <mi>T</mi>
              </math>
            </span>
            .`,
            "Therefore, when using two impedance heads, a single measurement is sufficient,<br />while two measurements with two known impedances are needed when using a single impedance head.",
          ],
          imageEquations: [
            `${config.ASSETS_URL}/01/case-01/slide02-view07-equation01.svg`,
            `${config.ASSETS_URL}/01/case-01/slide02-view07-equation02.svg`,
            `${config.ASSETS_URL}/01/case-01/slide02-view07-equation03.svg`,
          ],
        },
      ],
    },
  },
};

export default principle;
